import { useRef, useState, type FormEvent } from 'react';
import {
  cardLabel,
  DECK_IDS,
  DECKS,
  isRoomCode,
  normalizeRoomCode,
  ROOM_CODE_LENGTH,
  type DeckId,
  type RetroMode,
  type Role,
} from '@planning-session/shared';
import { createRetroRoom, createRoom, getRoomInfo } from '../lib/api.js';
import { rememberedName, rememberedRole } from '../lib/storage.js';
import { PlanningArgument } from './landing/PlanningArgument.js';
import { RetroArgument } from './landing/RetroArgument.js';
import { AlertIcon } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import styles from './Landing.module.css';

const HERO_COPY = {
  planning: {
    headline: 'Estimating out loud is agreeing with whoever spoke first.',
    headlineQuiet: 'This is the other way.',
    standfirst:
      'Everyone commits to a card face-down. Nothing turns over until the host says so, so you get four honest reads instead of one repeated four times.',
  },
  retro: {
    headline: 'Feedback with a name on it is whoever spoke first, agreed with.',
    headlineQuiet: 'This is the other way.',
    standfirst:
      'Everyone writes face-down. Nothing surfaces until the host reveals, so the board shows four honest reads before anyone groups them into a theme.',
  },
} as const;

/**
 * Author credit shown in the landing footer. A link renders only when its URL is
 * set, so leaving one blank simply omits it rather than shipping a dead link.
 */
const AUTHOR = {
  name: 'jmscarpa',
  github: 'https://github.com/jmscarpa',
  linkedin: 'https://linkedin.com/in/jmscarpa/',
} as const;

export interface LandingProps {
  readonly initialCode?: string;
  onEnter: (roomId: string, name: string, role: Role) => void;
  onEnterRetro: (roomId: string, name: string, role: Role) => void;
}

function deckPreview(deckId: DeckId): string {
  return DECKS[deckId].cards.slice(0, 5).map(cardLabel).join(' ');
}

export function Landing({
  initialCode = '',
  onEnter,
  onEnterRetro,
}: LandingProps) {
  const [name, setName] = useState(rememberedName.get);
  const [role, setRole] = useState<Role>(rememberedRole.get);
  const [deckId, setDeckId] = useState<DeckId>('fibonacci');
  const [tool, setTool] = useState<'planning' | 'retro'>('planning');
  const [retroMode, setRetroMode] = useState<RetroMode>('anonymous');
  const [retroTitle, setRetroTitle] = useState('');
  const [code, setCode] = useState(initialCode);
  const [busy, setBusy] = useState<'create' | 'join' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const trimmedName = name.trim();

  function enter(roomId: string) {
    rememberedName.set(trimmedName);
    rememberedRole.set(role);
    onEnter(roomId, trimmedName, role);
  }

  function enterRetro(roomId: string) {
    rememberedName.set(trimmedName);
    rememberedRole.set(role);
    onEnterRetro(roomId, trimmedName, role);
  }

  function requireName(): boolean {
    if (trimmedName.length > 0) return true;
    setError('Add your name first so the room knows who is voting.');
    nameRef.current?.focus();
    return false;
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!requireName()) return;

    setBusy('create');
    try {
      if (tool === 'retro') {
        enterRetro(
          await createRetroRoom(retroTitle.trim() || 'Retro', retroMode),
        );
      } else {
        enter(await createRoom(deckId));
      }
    } catch {
      setError('Could not start a session. Is the server running?');
    } finally {
      setBusy(null);
    }
  }

  async function handleJoin(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!requireName()) return;

    const roomId = normalizeRoomCode(code);
    if (!isRoomCode(roomId)) {
      setError(
        `A session code is ${ROOM_CODE_LENGTH} characters, like ABC234.`,
      );
      return;
    }

    setBusy('join');
    try {
      const info = await getRoomInfo(roomId);
      if (!info?.exists) {
        setError(
          `No live session called ${roomId}. Ask the host for a fresh code.`,
        );
        return;
      }
      if (info.kind === 'retro') {
        enterRetro(roomId);
      } else {
        enter(roomId);
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.field}>
        <div className={styles.sheet}>
          <header className={styles.masthead}>
            <span className={styles.markDot} aria-hidden="true" />
            <span className={styles.mark}>Planning Session</span>
            <span className={styles.mastheadSpacer} />
            <button
              type="button"
              className={styles.mastheadLink}
              onClick={() => nameRef.current?.focus({ preventScroll: false })}
            >
              Start a session
            </button>
          </header>

          <div
            className={styles.heroToolPicker}
            role="group"
            aria-label="Session type"
          >
            {(['planning', 'retro'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`${styles.toolOption} ${tool === option ? styles.toolOptionActive : ''}`}
                aria-pressed={tool === option}
                onClick={() => setTool(option)}
              >
                {option === 'planning' ? 'Planning session' : 'Retro board'}
              </button>
            ))}
          </div>

          <div className={styles.statement}>
            <h1 className={styles.headline}>
              {HERO_COPY[tool].headline}
              <span className={styles.headlineQuiet}>
                {' '}
                {HERO_COPY[tool].headlineQuiet}
              </span>
            </h1>
            <p className={styles.standfirst}>{HERO_COPY[tool].standfirst}</p>
          </div>

          <div className={styles.argumentBand}>
            {tool === 'retro' ? <RetroArgument /> : <PlanningArgument />}
          </div>
        </div>
      </div>

      <div className={`${styles.sheet} ${styles.paper}`}>
        <div className={styles.slipWrap}>
          <div className={styles.slip}>
            <form className={styles.form} onSubmit={handleCreate}>
              <div className={styles.slipHead}>
                <h2 className={styles.slipTitle}>Start a session</h2>
                <span className={styles.slipHint}>
                  You host: you reveal and move the room on.
                </span>
              </div>

              <label className={styles.row}>
                <span className={styles.label}>Your name</span>
                <input
                  ref={nameRef}
                  className={styles.input}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ada Lovelace"
                  autoComplete="nickname"
                  maxLength={40}
                />
              </label>

              {tool === 'planning' && (
                <div className={styles.row}>
                  <span className={styles.label}>Deck</span>
                  <div className={styles.decks} role="group" aria-label="Deck">
                    {DECK_IDS.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={`${styles.deck} ${id === deckId ? styles.deckSelected : ''}`}
                        aria-pressed={id === deckId}
                        onClick={() => setDeckId(id)}
                      >
                        <span className={styles.deckName}>
                          {DECKS[id].label}
                        </span>
                        <span className={styles.deckCards}>
                          {deckPreview(id)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {tool === 'retro' && (
                <>
                  <label className={styles.row}>
                    <span className={styles.label}>
                      What's this retro about?
                    </span>
                    <input
                      className={styles.input}
                      value={retroTitle}
                      onChange={(event) => setRetroTitle(event.target.value)}
                      placeholder="Sprint 24"
                      maxLength={200}
                    />
                  </label>

                  <div className={styles.roleLine}>
                    <span>Cards are</span>
                    <span
                      className={styles.roles}
                      role="group"
                      aria-label="Cards are"
                    >
                      {(['anonymous', 'visible'] as const).map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`${styles.role} ${retroMode === option ? styles.roleActive : ''}`}
                          aria-pressed={retroMode === option}
                          onClick={() => setRetroMode(option)}
                        >
                          {option === 'anonymous' ? 'Anonymous' : 'Visible'}
                        </button>
                      ))}
                    </span>
                  </div>
                </>
              )}

              <div className={styles.roleLine}>
                <span>Join as</span>
                <span
                  className={styles.roles}
                  role="group"
                  aria-label="Join as"
                >
                  {(['player', 'observer'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`${styles.role} ${role === option ? styles.roleActive : ''}`}
                      aria-pressed={role === option}
                      onClick={() => setRole(option)}
                    >
                      {option === 'player' ? 'Player' : 'Observer'}
                    </button>
                  ))}
                </span>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={busy !== null}
              >
                {busy === 'create' ? 'Dealing…' : 'Create session'}
              </Button>

              {error && (
                <p className={styles.error} role="alert">
                  <AlertIcon size={15} />
                  {error}
                </p>
              )}
            </form>

            <div className={styles.slipDivide} aria-hidden="true" />

            <form className={styles.form} onSubmit={handleJoin}>
              <div className={styles.slipHead}>
                <h2 className={styles.slipTitle}>Or join one</h2>
              </div>

              <label className={styles.row}>
                <span className={styles.label}>Session code</span>
                <input
                  className={`${styles.input} ${styles.code}`}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="ABC234"
                  spellCheck={false}
                  autoCapitalize="characters"
                  aria-describedby="code-hint"
                />
              </label>

              <p id="code-hint" className={styles.slipHint}>
                Paste the code or the whole link — either works.
              </p>

              <Button type="submit" size="large" disabled={busy !== null}>
                {busy === 'join' ? 'Joining…' : 'Take a seat'}
              </Button>
            </form>
          </div>
        </div>

        <div className={styles.facts}>
          <div className={styles.fact}>
            <span className={styles.factTerm}>Nothing to set up</span>
            <span className={styles.factCopy}>
              A link and a name is the whole thing. No accounts, no install, no
              workspace.
            </span>
          </div>
          <div className={styles.fact}>
            <span className={styles.factTerm}>
              Hidden on the server, not just the screen
            </span>
            <span className={styles.factCopy}>
              Other people's cards are never sent to your browser before the
              reveal — not hidden with CSS, not sent at all.
            </span>
          </div>
          <div className={styles.fact}>
            <span className={styles.factTerm}>Then it disappears</span>
            <span className={styles.factCopy}>
              Sessions live in memory and clear a couple of hours after the last
              person leaves. The estimate belongs in your tracker.
            </span>
          </div>
        </div>

        <footer className={styles.footer}>
          <span className={styles.footerMark}>Planning Session</span>
          <span className={styles.footerSpacer} aria-hidden="true" />
          <span className={styles.footerCredit}>
            Developed by {AUTHOR.name}
          </span>
          {(AUTHOR.github || AUTHOR.linkedin) && (
            <span className={styles.footerLinks}>
              {AUTHOR.github && (
                <a
                  className={styles.footerLink}
                  href={AUTHOR.github}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  GitHub
                </a>
              )}
              {AUTHOR.linkedin && (
                <a
                  className={styles.footerLink}
                  href={AUTHOR.linkedin}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  LinkedIn
                </a>
              )}
            </span>
          )}
        </footer>
      </div>
    </div>
  );
}
